import React from 'react';
import { VendorTopbar } from './VendorTopbar.js';
import { VendorSidebar } from './VendorSidebar.js';
import { ToastRegion, type ToastRegionProps } from '../../../shared/components/Toast.js';

export interface VendorShellProps {
  readonly children: React.ReactNode;
}

const ENV = (import.meta as unknown as { env?: { MODE?: string; VITE_APP_ENV?: string } }).env;

export function VendorShell({ children }: VendorShellProps): React.ReactElement {
  const [collapsed, setCollapsed] = React.useState(false);
  const [toasts, setToasts] = React.useState<ToastRegionProps['toasts']>([]);
  const dismissToast = React.useCallback(
    (id: string) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const env = ENV?.VITE_APP_ENV ?? ENV?.MODE ?? 'development';

  return (
    <div
      data-testid="vendor-shell"
      style={{
        display: 'grid',
        gridTemplateAreas: `"topbar topbar" "sidebar main"`,
        gridTemplateColumns: 'auto 1fr',
        gridTemplateRows: '56px 1fr',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--color-bg-canvas)',
        color: 'var(--color-text-primary)',
      }}
    >
      <a
        href="#main"
        data-testid="skip-to-main"
        style={{ position: 'absolute', left: -9999, top: 0, padding: 8, zIndex: 1000 }}
        onFocus={(e) => {
          e.currentTarget.style.left = '8px';
        }}
        onBlur={(e) => {
          e.currentTarget.style.left = '-9999px';
        }}
      >
        Skip to main content
      </a>
      <VendorTopbar env={env} onToggleSidebar={() => setCollapsed((v) => !v)} />
      <VendorSidebar collapsed={collapsed} />
      <main
        id="main"
        role="main"
        style={{ gridArea: 'main', padding: 16, overflowY: 'auto', minHeight: 0 }}
      >
        {children}
      </main>
      <ToastRegion toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
